import React from "react";
import Icon from "components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";

const RagTraderForm = ({ formData, errors, onInputChange, isValidating }) => {
  const { config } = useConfig();

  if (!config) return null; // Wait for config to load

  // Use material weight from Step 1 instead of separate input
  const ragTraderRevenue = (parseFloat(formData.materialWeight) || 0) * config.rag_trader_rate;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
          Rag Trader Revenue
        </h3>
        <p className="text-sm text-text-secondary">
          Calculate revenue potential from rag trader channel (Material Weight ×
          ${config.rag_trader_rate.toFixed(2)})
        </p>
      </div>

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {/* Material Weight - Read-only from Step 1 */}
        <div className="space-y-2">
          <label
            htmlFor="materialWeight"
            className="block text-sm font-body-medium text-text-primary"
          >
            <div className="flex items-center space-x-2">
              <Icon name="Weight" size={16} className="text-accent" />
              <span>Material Weight (LBS)</span>
              <span className="text-xs text-text-tertiary">(from Step 1)</span>
            </div>
          </label>

          <div className="relative">
            <input
              id="materialWeight"
              type="text"
              value={formData.materialWeight || ""}
              readOnly
              disabled
              className="block w-full px-4 py-3 border rounded-lg text-sm bg-secondary-100 text-text-secondary border-border cursor-not-allowed"
              aria-describedby="materialWeight-help"
            />
          </div>

          {/* Help Text */}
          <p id="materialWeight-help" className="text-xs text-text-tertiary">
            Material weight from Step 1 used to calculate rag trader revenue
            potential
          </p>
        </div>

        {/* Revenue Preview */}
        <div className="p-4 bg-accent-50 rounded-lg border border-accent-200">
          <div className="flex items-center space-x-3 mb-3">
            <Icon name="DollarSign" size={20} className="text-accent" />
            <h4 className="text-sm font-body-medium text-text-primary">
              Revenue Preview
            </h4>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary">Rate per pound:</span>
              <span className="font-data text-text-primary">${config.rag_trader_rate.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary">Material weight:</span>
              <span className="font-data text-text-primary">
                {formData.materialWeight || "0"} LBS
              </span>
            </div>
            <div className="pt-2 border-t border-accent-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-body-medium text-text-primary">
                  Total Rag Trader Revenue:
                </span>
                <span className="text-lg font-data text-text-primary">
                  $
                  {ragTraderRevenue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Information Card */}
        <div className="p-4 bg-surface-50 rounded-lg border border-border-light">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div>
              <h5 className="text-sm font-body-medium text-text-primary mb-1">
                About Rag Trader Revenue
              </h5>
              <p className="text-xs text-text-secondary">
                Rag trader revenue is calculated by multiplying the material
                weight by ${config.rag_trader_rate.toFixed(2)} per pound. This represents the potential income
                from selling textile waste to rag trading companies.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RagTraderForm;
